import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { ErrorBlock, Pill, useLoadable } from '../components/ui'
import type { ProjectNameCheck, ProjectOptions, ProjectResult } from '../types'

/* Start a project (3 Nov 2026). Cross-functional projects live in 01 RMS Group › 09 Projects. Anyone can start
   one: the portal's own account builds the folder and a team group, the person starting it leads it and owns the
   group, and the sponsoring director is told by email. Every check re-runs on the server when they press the button. */

const splitMembers = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean)

export default function StartProjectRoute({ me }: { me: string }) {
  const [options, retry] = useLoadable<ProjectOptions>(() => api.getProjectOptions(), () => false, [])
  const [name, setName] = useState('')
  const [nameCheck, setNameCheck] = useState<ProjectNameCheck | null>(null)
  const [sponsor, setSponsor] = useState('')
  const [members, setMembers] = useState('')
  const [confidential, setConfidential] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ProjectResult | null>(null)
  const [error, setError] = useState<{ message: string; correlationId?: string } | null>(null)

  const others = splitMembers(members).filter((m) => m.toLowerCase() !== me.toLowerCase())
  const ready = !!nameCheck?.ok && !!sponsor && others.length >= 1 && confidential !== null

  const checkName = async () => {
    if (!name.trim()) return
    try { setNameCheck(await api.checkProjectName(name)) } catch { setNameCheck(null) }
  }
  const start = async () => {
    if (!ready || confidential === null) return
    setBusy(true); setError(null)
    try {
      setResult(await api.startProject({ name, sponsor, members: others, confidential }))
    } catch (e) {
      const err = e as Error & { correlationId?: string }
      setError({ message: err.message, correlationId: err.correlationId })
    } finally {
      setBusy(false)
    }
  }

  if (options.status === 'error') return <ErrorBlock message={options.message} correlationId={options.correlationId} onRetry={retry} />

  if (result) {
    return (
      <>
        <p className="eyebrow">Start a project</p>
        <h1 style={{ marginBottom: 'var(--s-sm)' }}>Your project is ready</h1>
        <div className="block" style={{ borderLeft: '3px solid var(--rule)' }}>
          <h3 style={{ marginTop: 0 }}>{result.name}</h3>
          <p>
            In 01 RMS Group › 09 Projects{result.confidential ? ', open only to the project team' : ''}. Everyone on the team can add,
            move and tidy files there. You lead it: add or remove people any time in <Link to="/my-groups">My groups</Link>.
          </p>
          {result.memberFailures.length > 0 && (
            <p>
              <Pill tone="amber">Not added</Pill>{' '}
              {result.memberFailures.join(', ')}. Check the address and add them in My groups.
            </p>
          )}
          <p className="note">
            {result.sponsorNotified
              ? `${result.sponsor.name} (${result.sponsor.division}) has been told by email.`
              : `We could not email ${result.sponsor.name}; the RMS team will let them know.`}{' '}
            It shows in My access from tomorrow morning; until then, open it from the link below.
          </p>
          <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
            <a className="btn btn--secondary" href={result.webViewLink} target="_blank" rel="noreferrer">Open in Drive</a>
          </div>
        </div>
      </>
    )
  }

  const sponsors = options.status === 'ready' ? options.data.sponsors : []
  const subfolders = options.status === 'ready' ? options.data.subfolders : []

  return (
    <>
      <p className="eyebrow">Start a project</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>Start a project</h1>
      <p className="note" style={{ marginBottom: 'var(--s-lg)' }}>
        For work across teams or markets. The project gets its own folder in 01 RMS Group › 09 Projects
        ({subfolders.join(' · ')}) and its own team: only the people you add can open it. You lead the project and
        manage its team. Work inside one team stays in that team's own folders.
      </p>

      <section className="mv-step">
        <h2 className="wizard__sub">1. Name</h2>
        <label htmlFor="pj-name">Year and a short name</label>
        <div className="mv-row">
          <input id="pj-name" className="field" value={name} placeholder="2026 Pricing Review"
            onChange={(e) => { setName(e.target.value); setNameCheck(null) }} onBlur={checkName} />
        </div>
        {nameCheck && !nameCheck.ok && <p className="note" role="alert" style={{ marginTop: 'var(--s-xs)' }}>{nameCheck.message}</p>}
        {nameCheck?.ok && <p className="note" style={{ marginTop: 'var(--s-xs)' }}>Team group <code>{nameCheck.group}</code></p>}
      </section>

      <section className="mv-step">
        <h2 className="wizard__sub">2. Sponsor</h2>
        <label htmlFor="pj-sponsor">Sponsoring division</label>
        <select id="pj-sponsor" className="field" style={{ maxWidth: 640 }} value={sponsor} onChange={(e) => setSponsor(e.target.value)}>
          <option value="">Pick a division</option>
          {sponsors.map((s) => <option key={s.division} value={s.division}>{s.division} ({s.name})</option>)}
        </select>
        <p className="note" style={{ marginTop: 'var(--s-xs)' }}>The division's director is told about the project by email. No approval is needed.</p>
      </section>

      <section className="mv-step">
        <h2 className="wizard__sub">3. Team</h2>
        <label htmlFor="pj-members">d.light email addresses of the people on the project</label>
        <textarea id="pj-members" className="field" rows={4} style={{ maxWidth: 640, width: '100%' }} value={members}
          placeholder="name.surname@dlight.com, name.surname@dlight.com" onChange={(e) => setMembers(e.target.value)} />
        <p className="note" style={{ marginTop: 'var(--s-xs)' }}>
          You are on the team already. Add at least one other person; you can add more later in My groups.
        </p>
      </section>

      <section className="mv-step">
        <h2 className="wizard__sub">4. Confidential?</h2>
        <div className="mv-row" role="radiogroup" aria-label="Confidential project">
          <label style={{ fontWeight: 400 }}>
            <input type="radio" name="pj-conf" checked={confidential === false} onChange={() => setConfidential(false)} /> No
          </label>
          <label style={{ fontWeight: 400 }}>
            <input type="radio" name="pj-conf" checked={confidential === true} onChange={() => setConfidential(true)} /> Yes, only the team can open it
          </label>
        </div>
        <p className="note" style={{ marginTop: 'var(--s-xs)' }}>
          Not confidential: the team, plus Exco who can view and comment. Confidential: the team only.
        </p>
      </section>

      <section className="mv-step">
        <h2 className="wizard__sub">5. Start it</h2>
        <button className="btn btn--primary" disabled={!ready || busy} onClick={start}>
          {busy ? 'Setting up… (up to a minute)' : 'Start project'}
        </button>
        {!ready && <p className="note" style={{ marginTop: 'var(--s-xs)' }}>Fill in the four steps above first.</p>}
        {error && (
          <div className="block block--narrow" role="alert" style={{ marginTop: 'var(--s-sm)' }}>
            <Pill tone="red">Not started</Pill>
            <p style={{ marginTop: 'var(--s-sm)' }}>{error.message}</p>
            {error.correlationId && <code className="state__sub">{error.correlationId}</code>}
          </div>
        )}
      </section>
    </>
  )
}
